import StatusCode from 'status-code-enum';

import { adWriteApi } from 'api/adWriteApi';

import type {
  SendMessageSubmissionValues,
  UseSendMessageModal,
} from 'components/PageWrappers/AdDetails/components/SendMessageModal/SendMessageModal.typed';

import { format422ErrorsFromApiForForm } from 'helpers/forms';
import { fireToast } from 'helpers/Toasts';

import { useBraze } from 'hooks/useBraze';
import { pushToDataLayer } from 'services/gtmService';
import { rg4js } from 'helpers/raygun';
import { PAGE } from 'helpers/pages';

function useSendMessageModal(props: UseSendMessageModal) {
  const {
    adId,
    authorizationHeader,
    askToClose,
    setIsPostEnquirySimilarAdsModalOpen,
    sectionName,
    setSimilarAds,
    setIsSubmitting,
  } = props;
  const { logBrazeSendMessageEvent } = useBraze();

  const getSimilarAds = async () => {
    try {
      const { status, data } = await adWriteApi.getPostEnquirySimilarAds(
        adId,
        authorizationHeader,
      );
      askToClose();
      if (status === StatusCode.SuccessOK) {
        setSimilarAds(data);
        setIsPostEnquirySimilarAdsModalOpen(true);
      } else {
        rg4js('send', {
          error: new Error(
            'Getting similar ads did not return the expected status code',
          ),
          tags: [PAGE.AD_DETAILS],
          customData: { status },
        });
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
      }
    } catch (error) {
      rg4js('send', {
        error,
        customData: {
          functionName: 'getSimilarAds',
        },
        tags: [PAGE.AD_DETAILS],
      });
      askToClose();
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
    }
  };

  const onSubmit = async ({ message }: SendMessageSubmissionValues) => {
    setIsSubmitting(true);
    try {
      const sendMessagePayload = { message, id: adId };
      const { status } = await adWriteApi.sendMessage(
        sendMessagePayload,
        authorizationHeader,
      );
      if (status === StatusCode.SuccessCreated) {
        pushToDataLayer({ event: 'message_sent' });
        logBrazeSendMessageEvent(sectionName);
        getSimilarAds();
      } else {
        askToClose();
        rg4js('send', {
          error: new Error(
            'Send message submission did not return the expected status code',
          ),
          tags: [PAGE.AD_DETAILS],
          customData: { status },
        });
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
      }
    } catch (error) {
      rg4js('send', {
        error,
        customData: {
          functionName: 'onSubmit',
        },
        tags: [PAGE.AD_DETAILS],
      });
      if (
        error?.response?.status === StatusCode.ClientErrorUnprocessableEntity
      ) {
        setIsSubmitting(false);
        return format422ErrorsFromApiForForm(error.response.data);
      }
      askToClose();
      fireToast({
        type: 'ERROR',
        text: 'Sorry we couldn’t send your enquiry right now, please try again later',
      });
    }
  };

  return { onSubmit };
}

export { useSendMessageModal };
